import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { gql } from '@apollo/client/core';
import { apolloClient, setAuthToken, setLocale, setVersion } from '@/lib/apollo';
import { safeResetApolloStore } from '@/lib/apolloStoreReset';
import { BASE_URL, HOME_URL, DEFAULT_VERSION } from '@/lib/constants';
import { DEFAULT_LOCALE, detectSystemLocale, i18n, normalizeLocale, setI18nLanguage } from '@/composables/i18n';
import { createAccount, type CreateAccountInput } from '@/actions/auth/createAccount';
import { Preferences } from '@capacitor/preferences';
import type { Me } from '@/types/graphql';
import { useAppVersion } from '@/composables/useAppVersion';
import { useAppSettingsStore } from '@/stores/appSettings';
import { getAppPlatform } from '@/lib/platform';
import { setSentryUser, clearSentryUser } from '@/lib/setContexts';
import { buildValidationError } from '@/utils/validationErrors';

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

type AuthUser = Pick<Me, 'id' | 'name' | 'email' | 'locale' | 'profilePhotoUrl' | 'postsCount'> & {
  plot?: {
    id: string;
    postsCount: number;
  } | null;
};

type Credentials = {
  email: string;
  password: string;
};

async function readStoredValue(key: string): Promise<string | null> {
  try {
    const { value } = await Preferences.get({ key });
    if (value !== null) return value;
  } catch {
    // fall through to localStorage
  }

  if (typeof window !== 'undefined') {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  return null;
}

async function writeStoredValue(key: string, value: string | null) {
  if (value === null) {
    try {
      await Preferences.remove({ key });
    } catch {
      // ignore
    }

    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(key);
      } catch {
        // ignore
      }
    }

    return;
  }

  try {
    await Preferences.set({ key, value });
  } catch {
    // ignore
  }

  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // ignore
    }
  }
}

function parseStoredUser(value: string | null): AuthUser | null {
  if (!value) return null;

  try {
    return JSON.parse(value) as AuthUser;
  } catch {
    return null;
  }
}

function computeMobileLoginEndpoint(): string {
  return BASE_URL ? `${BASE_URL}/auth/login` : '/auth/login';
}

function computePwaLoginEndpoint(): string {
  return HOME_URL ? `${HOME_URL}/auth/pwa/login` : '/auth/pwa/login';
}

function computePwaLogoutEndpoint(): string {
  return HOME_URL ? `${HOME_URL}/auth/pwa/logout` : '/auth/pwa/logout';
}

function computeCsrfEndpoint(): string {
  return HOME_URL ? `${HOME_URL}/sanctum/csrf-cookie` : '/sanctum/csrf-cookie';
}

function getXsrfToken(): string | null {
  if (typeof document === 'undefined') return null;

  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

const AUTH_ME_QUERY = gql`
  query AuthMe {
    me {
      id
      name
      email
      locale
      profilePhotoUrl
      postsCount
      plot {
        id
        postsCount
      }
    }
  }
`;

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string>('');
  const user = ref<AuthUser | null>(null);
  const isReady = ref(false);
  const locale = ref(DEFAULT_LOCALE);
  const fullVersion = useAppVersion();
  const restorePromise = ref<Promise<boolean> | null>(null);
  const restoreCompleted = ref(false);

  // "web" here means browser/PWA session auth
  const isWebAuthMode = computed(() => getAppPlatform() === 'web');

  const isAuthenticated = computed(() => {
    return isWebAuthMode.value ? Boolean(user.value) : Boolean(token.value);
  });

  async function persistMobileUser(nextUser: AuthUser | null) {
    if (isWebAuthMode.value) return;
    await writeStoredValue(USER_KEY, nextUser ? JSON.stringify(nextUser) : null);
  }

  async function applyUser(nextUser: AuthUser | null) {
    user.value = nextUser;
    if (nextUser) {
      await changeLocale(nextUser.locale ?? DEFAULT_LOCALE);
      setSentryUser({
        id: nextUser.id,
        email: nextUser.email,
        name: nextUser.name,
      });
    } else {
      clearSentryUser();
    }

    await persistMobileUser(nextUser);
  }

  async function clearMobileTokenStorage() {
    token.value = '';
    setAuthToken(undefined);
    await writeStoredValue(TOKEN_KEY, null);
  }

  async function changeLocale(newLocale: string) {
    const normalizedLocale = normalizeLocale(newLocale);

    locale.value = normalizedLocale;
    setLocale(normalizedLocale);
    setI18nLanguage(i18n, normalizedLocale);

    if (user.value) {
      user.value = { ...user.value, locale: normalizedLocale };
      await persistMobileUser(user.value);
    }
  }

  async function handleAppOpen() {
    const platform = getAppPlatform();

    await apolloClient.mutate({
      mutation: gql`
        mutation HandleAppOpen($platform: String!, $deviceInfo: Mixed!) {
          handleAppOpen(platform: $platform, deviceInfo: $deviceInfo) {
            id
          }
        }
      `,
      variables: {
        platform,
        deviceInfo: {},
      },
    });
  }

  async function initialize() {
    const [tokenValue, userValue] = await Promise.all([
      readStoredValue(TOKEN_KEY),
      readStoredValue(USER_KEY),
    ]);

    if (isWebAuthMode.value) {
      // Web/PWA uses server session only; never restore bearer token or cached user as auth source.
      token.value = '';
      setAuthToken(undefined);
      user.value = null;

      if (tokenValue) {
        await writeStoredValue(TOKEN_KEY, null);
      }
    } else {
      user.value = parseStoredUser(userValue);

      if (tokenValue) {
        token.value = tokenValue;
        setAuthToken(tokenValue);
      }

      if (user.value) {
        setSentryUser({
          id: user.value.id,
          email: user.value.email,
          name: user.value.name,
        });
      }
    }

    setVersion(fullVersion.value);

    const resolvedLocale = normalizeLocale(user.value?.locale ?? detectSystemLocale());
    locale.value = resolvedLocale;
    setLocale(resolvedLocale);
    setI18nLanguage(i18n, resolvedLocale);

    isReady.value = true;
  }

  async function hydrateCurrentUser(): Promise<AuthUser | null> {
    try {
      const { data } = await apolloClient.query<{ me: AuthUser | null }>({
        query: AUTH_ME_QUERY,
        fetchPolicy: 'network-only',
      });

      const me = data?.me ?? null;
      await applyUser(me);

      return me;
    } catch {
      await applyUser(null);
      return null;
    }
  }

  async function ensurePwaCsrfCookie() {
    const endpoint = computeCsrfEndpoint();

    const response = await fetch(endpoint, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'X-App-Version': DEFAULT_VERSION,
        'X-App-Platform': getAppPlatform(),
      },
    });

    if (!response.ok) {
      throw new Error('Unable to initialize secure session.');
    }
  }

  async function login(credentials: Credentials) {
    if (isWebAuthMode.value) {
      await clearMobileTokenStorage();
      await ensurePwaCsrfCookie();

      const xsrfToken = getXsrfToken();

      if (!xsrfToken) {
        throw new Error('Missing CSRF token.');
      }

      const endpoint = computePwaLoginEndpoint();
      let response: Response;

      try {
        response = await fetch(endpoint, {
          method: 'POST',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': xsrfToken,
            'X-App-Version': DEFAULT_VERSION,
            'X-App-Platform': getAppPlatform(),
          },
          body: JSON.stringify(credentials),
        });
      } catch {
        throw new Error('Unable to reach the authentication server.');
      }

      const responseBody = (await response.json().catch(() => null)) as Record<string, any> | null;

      if (!response.ok) {
        throw buildValidationError(responseBody, 'The provided credentials are incorrect.', response.status);
      }

      const me = await hydrateCurrentUser();

      if (!me) {
        throw new Error('Authentication succeeded but no user session was returned.');
      }

      await useAppSettingsStore().refreshFromApi();

      try {
        await handleAppOpen();
      } catch (e) {
        console.error('Failed to handle app open after login:', e);
      }

      console.log('Welcome back, ' + (user.value?.name ?? 'user') + '!');
      return;
    }

    const endpoint = computeMobileLoginEndpoint();
    let response: Response;

    try {
      response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-App-Version': DEFAULT_VERSION,
          'X-App-Platform': getAppPlatform(),
        },
        body: JSON.stringify(credentials),
      });
    } catch {
      throw new Error('Unable to reach the authentication server.');
    }

    const responseBody = (await response.json().catch(() => null)) as Record<string, any> | null;

    if (!response.ok) {
      throw buildValidationError(responseBody, 'The provided credentials are incorrect.', response.status);
    }

    const payload = responseBody ?? {};

    if (!payload.token) {
      throw new Error('Authentication succeeded but no token was returned.');
    }

    token.value = payload.token;
    setAuthToken(payload.token);

    await writeStoredValue(TOKEN_KEY, payload.token);
    await applyUser((payload.user ?? null) as AuthUser | null);

    await useAppSettingsStore().refreshFromApi();

    try {
      await handleAppOpen();
    } catch (e) {
      console.error('Failed to handle app open after login:', e);
    }

    console.log('Welcome back, ' + (user.value?.name ?? 'user') + '!');
  }

  async function register(input: CreateAccountInput) {
    return await createAccount({
      ...input,
      locale: normalizeLocale(input.locale || locale.value),
    });
  }

  async function logout() {
    restoreCompleted.value = false;
    restorePromise.value = null;
    
    if (isWebAuthMode.value) {
      try {
        await ensurePwaCsrfCookie();

        await fetch(computePwaLogoutEndpoint(), {
          method: 'POST',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'X-XSRF-TOKEN': getXsrfToken() ?? '',
            'X-App-Version': DEFAULT_VERSION,
            'X-App-Platform': getAppPlatform(),
          },
        });
      } catch (e) {
        console.error('Failed to logout PWA session:', e);
      }
    }

    token.value = '';
    user.value = null;
    setAuthToken(undefined);

    await Promise.all([
      writeStoredValue(TOKEN_KEY, null),
      writeStoredValue(USER_KEY, null),
    ]);

    try {
      await safeResetApolloStore();
      clearSentryUser();
    } catch (e) {
      console.error('Failed to clear Apollo store on logout:', e);
    }
  }

  async function attemptRestore(): Promise<boolean> {
    // Already done — return immediately without any async work
    if (restoreCompleted.value) {
      console.log('[auth] attemptRestore: skipped (already completed)');
      return isAuthenticated.value;
    }

    // In-flight — share the existing promise
    if (restorePromise.value) {
      console.log('[auth] attemptRestore: awaiting in-flight promise');
      return await restorePromise.value;
    }

    restorePromise.value = (async () => {
      console.log('[auth] attemptRestore:start');

      await initialize();

      if (isWebAuthMode.value) {
        console.log('[auth] web mode: hydrating current user');
        const me = await hydrateCurrentUser();
        console.log('[auth] hydrateCurrentUser result:', me);

        if (!me) {
          token.value = '';
          setAuthToken(undefined);
        }
      }

      console.log('[auth] isAuthenticated:', isAuthenticated.value);

      if (isAuthenticated.value) {
        try {
          await handleAppOpen();
        } catch (e) {
          console.error('Failed to handle app open on restore:', e);
        }
      }

      return isAuthenticated.value;
    })();

    try {
      return await restorePromise.value;
    } finally {
      restoreCompleted.value = true; // ← mark done before clearing promise
      restorePromise.value = null;
    }
  }

  return {
    token,
    user,
    isReady,
    locale,
    isAuthenticated,
    changeLocale,
    handleAppOpen,
    initialize,
    login,
    register,
    logout,
    attemptRestore,
  };
});
